// A small priority pool for GOES reads. Each slot runs one job at a time.
// Requests with the same key share one job. A queued job is dropped once every
// caller has aborted; a running job still finishes so `finalize` can cache it.

export const GOES_PRIORITY = Object.freeze({ current: 0, loop: 1, prefetch: 2 });
const MAX_AFFINITY = 64;

function rankOf(priority) {
  return GOES_PRIORITY[priority] ?? GOES_PRIORITY.prefetch;
}

function abortError() {
  return new DOMException("Aborted", "AbortError");
}

export function goesPoolSize(nav = globalThis.navigator) {
  const cores = nav?.hardwareConcurrency || 2;
  const memory = nav?.deviceMemory || 4;
  if (cores <= 2 || memory <= 2) return 1;
  if (cores >= 8 && memory >= 4) return 3;
  return 2;
}

export function createTaskPool({ size = 1, execute }) {
  const slots = Math.max(1, size | 0);
  const busy = new Array(slots).fill(false);
  const jobs = new Map();
  const queue = [];
  const affinity = new Map();
  let order = 0;

  function slotFor(job) {
    if (job.affinity == null) return null;
    const slot = affinity.get(job.affinity);
    return slot == null ? null : slot;
  }

  function claim(job, slot) {
    if (job.affinity == null) return;
    affinity.delete(job.affinity);
    affinity.set(job.affinity, slot);
    while (affinity.size > MAX_AFFINITY) affinity.delete(affinity.keys().next().value);
  }

  function nextFor(slot) {
    let best = -1;
    for (let i = 0; i < queue.length; i++) {
      const job = queue[i];
      const owner = slotFor(job);
      if (owner != null && owner !== slot) continue;
      if (best < 0 || job.rank < queue[best].rank
        || (job.rank === queue[best].rank && job.order < queue[best].order)) {
        best = i;
      }
    }
    return best < 0 ? null : queue.splice(best, 1)[0];
  }

  function settle(job, method, value) {
    jobs.delete(job.key);
    for (const waiter of job.waiters) {
      waiter.detach();
      waiter[method](value);
    }
    job.waiters.clear();
  }

  function start(job, slot) {
    busy[slot] = true;
    job.running = true;
    claim(job, slot);
    Promise.resolve()
      .then(() => execute(slot, job.payload))
      .then((result) => (job.finalize ? job.finalize(result) : result))
      .then(
        (result) => settle(job, "resolve", result),
        (err) => settle(job, "reject", err),
      )
      .finally(() => {
        busy[slot] = false;
        pump();
      });
  }

  function pump() {
    for (let slot = 0; slot < slots; slot++) {
      if (busy[slot]) continue;
      const job = nextFor(slot);
      if (job) start(job, slot);
    }
  }

  function removeWaiter(job, waiter) {
    job.waiters.delete(waiter);
    if (job.waiters.size || job.running) return;
    const idx = queue.indexOf(job);
    if (idx >= 0) queue.splice(idx, 1);
    jobs.delete(job.key);
  }

  function run(key, payload, { priority = "prefetch", signal, affinity: pin, finalize } = {}) {
    if (signal?.aborted) return Promise.reject(abortError());
    let job = jobs.get(key);
    if (job) {
      job.rank = Math.min(job.rank, rankOf(priority));
    } else {
      job = {
        key,
        payload,
        finalize,
        affinity: pin ?? null,
        rank: rankOf(priority),
        order: order++,
        running: false,
        waiters: new Set(),
      };
      jobs.set(key, job);
      queue.push(job);
    }
    const target = job;
    const promise = new Promise((resolve, reject) => {
      const waiter = { resolve, reject, detach: () => {} };
      if (signal) {
        const onAbort = () => {
          removeWaiter(target, waiter);
          reject(abortError());
        };
        signal.addEventListener("abort", onAbort, { once: true });
        waiter.detach = () => signal.removeEventListener("abort", onAbort);
      }
      target.waiters.add(waiter);
    });
    pump();
    return promise;
  }

  return {
    run,
    get queued() { return queue.length; },
    get running() { return busy.filter(Boolean).length; },
  };
}
